import { createQueryKeys } from "@lukemorales/query-key-factory";
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { api } from "@/lib/api";
import type { Paginate, User } from "@/types";

import { conversations } from "./conversation";

export interface CreateGroupRequest {
  name: string;
  type: "public" | "private";
  image: string | null;
  user_ids: number[];
}

export type GroupOwner = User;

export interface CreatedGroup {
  id: number;
  name: string;
  type: "public" | "private";
  image_url: string | null;
  image_thumb: string | null;
  invite_token: string;
  owner: GroupOwner;
  can_join: boolean;
  can_leave: boolean;
}

export interface GroupDetails {
  id: number;
  name: string;
  type: "public" | "private";
  image_url: string | null;
  image_thumb: string | null;
  invite_token: string;
  owner: User;
  can_join: boolean;
  can_leave: boolean;
  can_update: boolean;
  can_delete: boolean;
}

export interface GroupParticipant extends User<true> {
  is_owner: boolean;
  is_manager?: boolean;
}

export interface UpdateGroupRequest {
  name: string;
  type: "public" | "private";
  image?: string | null;
}

export interface SyncParticipantsRequest {
  user_ids: number[];
}

export const groups = createQueryKeys("messenger-groups", {
  one: (id?: number) => [id],
  participants: (id?: number) => [id],
});

const participantsInclude = "position";
const participantsPerPage = 1000;

const invalidateGroupQueries = (
  queryClient: ReturnType<typeof useQueryClient>,
  groupId: number,
) => {
  queryClient.invalidateQueries(groups.participants(groupId));
  queryClient.invalidateQueries(groups.one(groupId));
  queryClient.invalidateQueries({
    queryKey: conversations.allInfinite._def,
  });
};

export const useCreateGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateGroupRequest) =>
      api
        .post<CreatedGroup>("/messenger/groups", data)
        .then((response) => response.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: conversations.allInfinite._def,
      });
    },
  });
};

export const fetchGroupDetailsQueryOptions = (groupId?: number) =>
  queryOptions({
    ...groups.one(groupId),
    queryFn: () =>
      api
        .get<GroupDetails>(`/messenger/groups/${groupId}`)
        .then((response) => response.data),
    enabled: groupId !== undefined,
  });

export const useFetchGroupDetails = (groupId?: number) =>
  useQuery(fetchGroupDetailsQueryOptions(groupId));

export const fetchGroupParticipantsQueryOptions = (groupId?: number) =>
  queryOptions({
    ...groups.participants(groupId),
    queryFn: () =>
      api
        .get<
          Paginate<GroupParticipant>
        >(`/messenger/groups/${groupId}/participants`, { params: { include: participantsInclude, per_page: participantsPerPage } })
        .then((response) => response.data),
    enabled: groupId !== undefined,
  });

export const useFetchGroupParticipants = (groupId?: number) =>
  useQuery(fetchGroupParticipantsQueryOptions(groupId));

export const useUpdateGroup = (groupId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateGroupRequest) =>
      api
        .put<GroupDetails>(`/messenger/groups/${groupId}`, data)
        .then((response) => response.data),
    onSuccess: () => {
      invalidateGroupQueries(queryClient, groupId);
    },
  });
};

export const useDeleteGroup = (groupId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => api.delete(`/messenger/groups/${groupId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: conversations.allInfinite._def,
      });
      queryClient.invalidateQueries(groups.one(groupId));
    },
  });
};

export const useLeaveGroup = (groupId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => api.delete(`/messenger/groups/${groupId}/leave`),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: conversations.allInfinite._def,
      });
      queryClient.invalidateQueries(groups.one(groupId));
    },
  });
};

export const useSyncGroupParticipants = (groupId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: SyncParticipantsRequest) =>
      api
        .post<boolean>(`/messenger/groups/${groupId}/participants/sync`, data)
        .then((response) => response.data),
    onSuccess: () => {
      invalidateGroupQueries(queryClient, groupId);
    },
  });
};
